import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";

import type { SelectedAddressBookItems } from "~/features/quote/context/quote-address-context";
import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { formatQuoteState } from "~/features/quote/utils/format-quote-state.ts";
import { mapPostQuoteCommoditiesToHandlingUnits } from "~/features/quote/utils/map-post-quote-commodity-to-handling-unit.ts";
import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import type { QuoteBody } from "~/shared/schemas/quote-schema";
import {
	quoteBodySchema,
	quoteResponseSchema,
} from "~/shared/schemas/quote-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";
import type { QuoteStateType } from "~/shared/types/quote-details-types";

const postSimulatedQuote = async (quoteDetails: QuoteBody) => {
	console.log("Post Simulated Quote");
	const url = apiRoutes.SIMULATEQUOTE;
	const response = await postAndValidate(
		url,
		quoteDetails,
		quoteResponseSchema,
	);
	if (!response.success) {
		throw new Error(
			`Failed to simulate quote: ${response.error}`,
			response.details instanceof Error
				? { cause: response.details }
				: undefined,
		);
	}
	console.log("Simulated Quote Response", response.data);
	return response.data;
};

export const usePostSimulatedQuote = () => {
	const { mergeResponseIntoQuoteState } = useContext(QuoteDetailsContext);
	const { showError, showSuccess } = useNotificationActions();

	const { mutateAsync: createSimulatedQuote, isPending } = useMutation({
		mutationFn: async ({
			quoteDetails,
			selectedAddressBookItems,
		}: {
			quoteDetails: QuoteStateType;
			selectedAddressBookItems: SelectedAddressBookItems;
		}) => {
			const body = formatQuoteState(
				quoteDetails,
				selectedAddressBookItems,
				true,
			);
			const parsedJson = quoteBodySchema.safeParse(body);
			console.log("Parsed Quote Body", parsedJson);
			if (!parsedJson.success) {
				throw new Error(`Invalid quote data: ${parsedJson.error.message}`, {
					cause: parsedJson.error,
				});
			}
			const data = await postSimulatedQuote(parsedJson.data);
			const handlingUnits = mapPostQuoteCommoditiesToHandlingUnits(
				data.quoteCommodities,
			);
			mergeResponseIntoQuoteState({
				id: data.id,
				carrierPrices: data.carrierPrices,
				items: handlingUnits,
				eTag: data.eTag,
				createdDate: data.createdDate,
				expirationDate: data.expirationDate,
				publicGuid: data.publicGuid,
				hasRoutingGuideMatch: data.hasRoutingGuideMatch,
			});
		},

		onSuccess: () => {
			showSuccess("Simulated quote rates fetched successfully");
		},

		onError: (_err) => {
			showError(`${_err}`);
		},
	});

	return { createSimulatedQuote, isPending };
};
